import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import useScheduleTable from '../RoomScheduleTable/useScheduleTable';
import { RoomSchedule, RoomV2 } from 'type/room';
import { IMeetingOrderFn, IMeetingOrderForm } from 'type/meeting';
import { closeMeetingV2, orderMeeting, setMeetingOrderParams } from 'model/meeting';
import { useDispatch } from 'react-redux';
import { IMeetingDetail } from 'type/meetingDetail';
import tracker from 'lib/tracker';
import { cloneDeep } from 'lodash-es';
import { includeMasterInMeetingMembers } from 'util/meeting';
import { createMeetingOrderPointHubSuccessResult } from 'lib/pointHub';
import { Modal } from 'antd';

interface IProps {
  room: RoomV2;
}

interface RoomScheduleSingleRef {
  currentDate: number | string | Date;
  room: RoomV2;
}

const SingleRoomScheduleTable: React.FC<IProps> = ({ room }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { componentRef, setBlinkList, emitScheduleChange } = useScheduleTable<RoomScheduleSingleRef>();

  /** 点击日程 */
  const onClickOrderSchedule = ({ detail }: { detail: [RoomSchedule] }) => {
    const [roomSchedule] = detail;
    tracker?.reporteMeetingRoom('查看会议详情');
    history.push({ pathname: '/details/' + roomSchedule.meetingNo });
  };

  /** 点击提前结束 */
  const onClickConcludeSchedule = useCallback(({ detail }: { detail: [RoomSchedule] }) => {
    const [roomSchedule] = detail;
    if (!roomSchedule?.meetingNo) return;

    Modal.confirm({
      title: '提前结束会议',
      icon: '',
      content: '确定要提前结束此会议吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        tracker?.reporteMeetingRoom('提前结束');
        dispatch(closeMeetingV2(roomSchedule.meetingNo));
        setTimeout(() => {
          emitScheduleChange();
        }, 1000);
      },
      centered: true,
      zIndex: 10000,
      autoFocusButton: null
    });
  }, [dispatch, emitScheduleChange]);

  /** 点击预约 */
  const onConfirm: IMeetingOrderFn = (meeting) => {
    const meetingWithMaster = includeMasterInMeetingMembers(meeting);
    tracker?.reporteMeetingRoom('预约会议');
    return new Promise((resolve) => {
      dispatch(
        orderMeeting({
          meeting: meetingWithMaster,
          onSuccess: (meeting: IMeetingDetail) => {
            emitScheduleChange();
            setBlinkList(meeting.roomOrderList?.map((room) => room.orderNo) || []);
            resolve(createMeetingOrderPointHubSuccessResult(meeting));
          },
          onFail: (err: any) => {
            if (err?.response?.data?.code === 400403002) {
              const data = err?.response?.data?.data;
              resolve({ success: false, data: data });
            } else if (err?.response?.data?.code === 400406011) {
              resolve({ success: false, errMesaage: '预约人（部门）、会议主题、会议地点可能包含涉黄/涉暴/政治/敏感信息，请检查后重试' });
            } else {
              resolve({ success: false, data: null });
            }
          },
          redirect: false
        })
      );
    });
  };

  /** 点击预约表单-更多 */
  const onClickOrderMore = (form: IMeetingOrderForm) => {
    tracker?.reporteMeetingRoom('会议室-点击预约表单-更多');
    dispatch(setMeetingOrderParams(cloneDeep(form)));
    history.push('/order-meeting');
  };

  /** 会议室信息 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.room = room;
  }, [room]);

  const onRoomImageClick = () => {
    tracker?.reporteMeetingRoom('查看会议室图片');
  };

  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.confirm = onConfirm;
    componentRef.current.clickMore = onClickOrderMore;
    componentRef.current?.addEventListener('click-schedule-detail', onClickOrderSchedule);
    componentRef.current?.addEventListener('click-schedule-conclude', onClickConcludeSchedule);
    componentRef.current?.addEventListener('on-room-image-click', onRoomImageClick);
    componentRef.current.aiRecommendEnabled = false;

    // 卸载时-清除监听
    return () => {
      componentRef.current?.removeEventListener('click-schedule-detail', onClickOrderSchedule);
      componentRef.current?.removeEventListener('click-schedule-conclude', onClickConcludeSchedule);
      componentRef.current?.removeEventListener('on-room-image-click', onRoomImageClick);
    };
  }, [onClickConcludeSchedule]);

  return (
    <>
      <div style={{ width: '100%', height: '100%', margin: 'auto' }}>
        <ce-room-schedule-single theme="client" ref={componentRef} />
      </div>
    </>
  );
};

export default SingleRoomScheduleTable;
